import classNames from "classnames";
import { decamelizeKeys } from "humps";
import { useActivityAvailabilityPastDaysLimit } from "PFApp/hooks";
import { letFilterRestore } from "PFApp/use_filtered_collection";
import { AvailabilityInput, DURATION_MODES } from "PFComponents/availability";
import { utc } from "PFCore/helpers/date";
import { getAttribute } from "PFCore/helpers/get_attribute";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { AvailabilityRuleV2, FilterWithDefaults, Template } from "PFTypes";

import css from "./availability_filter.module.scss";
import { useAvailabilityDefaults } from "./hooks/use_availability_defaults";
import { useAvailabilityState } from "./hooks/use_availability_state";
import { AvailabilityLimitWrapper } from "./parts/availability_limit_wrapper";

type AvailabilityFilterItemProps = {
  filter: FilterWithDefaults;
  handleChange: (updated: Partial<AvailabilityRuleV2> | null) => void;
  scope?: number;
  templateKey?: string;
  allowClearRestore?: boolean;
  onValidate?: (error?: string) => void;
  disabled?: boolean;
};

export const AvailabilityFilterItem = ({
  filter,
  handleChange,
  scope = 0,
  templateKey = "task",
  allowClearRestore = true,
  disabled
}: AvailabilityFilterItemProps) => {
  // TODO: [SP-6425] Use camelCase in all filter items when all pages use Filters V2
  const decamelizedFilter = decamelizeKeys(filter) as FilterWithDefaults;

  const { formatISODate } = useDateFormatter();
  const {
    data: { availability: accountAvailability }
  } = useMyAccountInfo();
  const activityAvailabilityPastDaysLimit = useActivityAvailabilityPastDaysLimit();

  const { availability, updateAvailability, restoreAvailability, errors, isValidating } =
    useAvailabilityState(decamelizedFilter, handleChange);

  const { defaultAvailability, allowedModes } = useAvailabilityDefaults(
    decamelizedFilter,
    templateKey as Template["key"]
  );

  const activityAvailabilityPastDaysLimitISODate = formatISODate(
    utc().subtract(activityAvailabilityPastDaysLimit, "days")
  );
  const minDate = {
    start: activityAvailabilityPastDaysLimitISODate,
    end: activityAvailabilityPastDaysLimitISODate
  };

  return (
    <div className={classNames(css.root)}>
      <AvailabilityLimitWrapper scope={scope}>
        <AvailabilityInput
          availability={availability}
          accountAvailability={accountAvailability}
          defaultAvailabilityMode={defaultAvailability.mode || undefined}
          defaultAvailabilityThreshold={
            getAttribute(defaultAvailability, "availability_threshold") || undefined
          }
          allowedModes={allowedModes}
          allowedDurationModes={DURATION_MODES}
          onChange={updateAvailability}
          onRestore={restoreAvailability}
          minDate={minDate}
          allowClear={allowClearRestore && !filter.required}
          allowRestore={allowClearRestore && letFilterRestore(filter)}
          errors={errors}
          isDisabled={disabled}
          isValidating={isValidating}
          isFilter
        />
      </AvailabilityLimitWrapper>
    </div>
  );
};
